import { useEffect, useMemo, useState, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { Switch } from "@/components/shadcn/switch";
import { Skeleton } from "@/components/shadcn/skeleton";
import { cn } from "@/lib/utils";

import { Banner } from "../Banner";
import { useQuery } from "@tanstack/react-query";
import { SpotflowService } from "@/services/Spotflow/spotflow";
import { ModelDefinition } from "@/generated";
import ImageWithFallback from "../ImageWithFallback";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { normalizeValue } from "@/utils/generic";
import ErrorBoundary from "../ErrorBoundary";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { SearchBar } from "../SearchBar";

interface IModelExtended extends ModelDefinition {
  id: string;
  speed: number;
  quality: number;
  normalizedSpeed: number;
}

interface IModelToggleField {
  name: string;
  endpoint: string;
  modelType: string;
  defaultValue?: string[];
  required: boolean;
  label?: string;
  isDisabled?: boolean;
}

interface IModelToggleItem {
  model: IModelExtended;
  isChecked: boolean;
  onChange: (checked: boolean) => void;
  isDisabled?: boolean;
}

const ModelToggleItem = ({
  model,
  isChecked,
  onChange,
  isDisabled,
}: IModelToggleItem) => {
  return (
    <div
      className={cn(
        "flex items-center gap-2 w-full py-3 rounded-md px-2 transition-colors overflow-y-visible",
        isDisabled
          ? "cursor-not-allowed opacity-50"
          : "cursor-pointer hover:bg-accent/50"
      )}
      onClick={() => !isDisabled && onChange(!isChecked)}
    >
      <div className="relative h-4 w-4 shrink-0">
        <ImageWithFallback
          fill
          src={model.image || "/images/placeholder.svg"}
          alt={`Logo for ${model.title}`}
          className="dark:invert dark:brightness-0"
        />
      </div>
      <span className="font-medium leading-normal !text-sm text-primary truncate">
        {model.title}
      </span>
      <Switch
        checked={isChecked}
        onCheckedChange={onChange}
        disabled={isDisabled}
        className="ml-auto"
      />
    </div>
  );
};

export const ModelToggleField = ({
  name,
  endpoint,
  modelType,
  defaultValue = [],
  required,
  label,
  isDisabled,
}: IModelToggleField) => {
  const form = useFormContext();

  /* Fetch available models */
  const {
    data: modelsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [DATA_KEYS.MODELS, endpoint],
    queryFn: () => SpotflowService.getModels(endpoint),
    retry: false,
    staleTime: 30_000,
  });

  /* Transform models data to extended format */
  const modelsUpdatedData: IModelExtended[] = useMemo(() => {
    if (!modelsData) {
      return [];
    }

    const visibleModels = modelsData
      .filter((model) => model.visible !== false)
      .sort((a, b) => a.name.localeCompare(b.name));
    const qualityArray = visibleModels.map((el) => el.properties.quality);
    const speedArray = visibleModels.map((el) => el.properties.speed);

    return (
      visibleModels?.map((model) => ({
        id: model.name,
        ...model,
        speed: model.properties.speed,
        quality: model.properties.quality,
        normalizedSpeed: normalizeValue(
          model.properties.speed,
          speedArray,
          modelType === "text" ? 214 : 2
        ),
      })) || []
    );
  }, [modelsData, modelType]);

  /* Get initial selected models from form or default value */
  const getInitialSelectedModels = (): string[] => {
    const formValue = form.getValues(name) as string[];
    if (formValue && Array.isArray(formValue)) {
      return formValue;
    }
    return defaultValue || [];
  };

  const [selectedModels, setSelectedModels] = useState<string[]>(() =>
    getInitialSelectedModels()
  );

  const [search, setSearch] = useState("");
  const filteredModels = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return modelsUpdatedData;
    return modelsUpdatedData.filter((m) =>
      (m.title || m.name).toLowerCase().includes(q)
    );
  }, [modelsUpdatedData, search]);

  /* Update form when selected models change */
  const updateFormValue = useCallback(
    (models: string[]) => {
      const currentFormValue = form.getValues(name) as string[];
      const currentValueString = JSON.stringify(currentFormValue?.sort() || []);
      const selectedValueString = JSON.stringify(models.sort());

      if (currentValueString !== selectedValueString) {
        form.setValue(name, models);
      }
    },
    [form, name]
  );

  useEffect(() => {
    updateFormValue(selectedModels);
  }, [selectedModels, updateFormValue]);

  /* Handle individual model toggle */
  const handleModelToggle = (modelName: string, checked: boolean) => {
    setSelectedModels((prev) => {
      if (checked) {
        return [...prev, modelName];
      } else {
        return prev.filter((name) => name !== modelName);
      }
    });
  };

  if (isError) {
    // Field is hidden on fetch error — the parent dialog already surfaces a
    // top-level loading/error state for the provider, so showing a second
    // banner here was redundant and confused users (looked like leftover UI).
    return null;
  }

  // Keep skeleton up until react-query returns a real array. ``isLoading``
  // briefly reads false on initial render before the fetch resolves, and
  // ``modelsUpdatedData`` is always ``[]`` (never null) so the original
  // ``!modelsUpdatedData`` guard never fired — letting an empty grid
  // render for one tick.
  if (isLoading || modelsData === undefined) {
    return (
      <div>
        {label && <NoxusFormLabel label={label} name={name} />}
        <div className="w-full flex flex-col gap-0.5">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary
      fallback={
        <Banner
          label="There was an issue loading the model toggles. Please refresh the page and try again."
          style="error"
        />
      }
    >
      <div className="flex flex-col">
        {label && <NoxusFormLabel label={label} name={name} />}
        {/* Sticky search bar.
            - z-20 so it sits above adjacent siblings in the same stacking
              context (model rows, sibling model-toggle fields).
            - py-3 (instead of just pb-3) plus a -mt-3 makes the opaque
              `bg-background` area extend slightly above and below the input,
              covering the gap where rows would otherwise peek out as they
              scroll past the bar's top/bottom edge. */}
        <div className="sticky -top-4 z-20 -mt-10 bg-background py-3">
          <SearchBar
            value={search}
            setValue={setSearch}
            placeholder="Search models..."
            isDebounced
            isClearable
          />
        </div>
        <div
          className="w-full flex flex-col overflow-y-auto p-5 pt-4 pr-1 -mr-1"
          style={{ scrollbarGutter: "stable" }}
        >
          {filteredModels.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No models match &ldquo;{search}&rdquo;.
            </p>
          ) : (
            filteredModels.map((model) => (
              <ModelToggleItem
                key={model.name}
                model={model}
                isChecked={selectedModels.includes(model.name)}
                onChange={(checked) => handleModelToggle(model.name, checked)}
                isDisabled={isDisabled}
              />
            ))
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};
